import * as _ from 'lodash';

import { ClonableItem } from '../../../shared/models/clonable-item';
import { ZoningCodeVersion } from '.';

import IZoningCode = ZoningAPI.IZoningCode;
import IZoningCodeStatistic = ZoningAPI.IZoningCodeStatistic;
import IZoningCodeRecord = ZoningAPI.IZoningCodeRecord;
import { ITaskModel } from 'src/app/shared/interfaces/task-model.interface';
import * as moment from 'moment';
import { DATE_FORMAT } from 'src/app/shared/utils/utils';
import { ZoningCodeStatus, getPrettyZCStatus } from 'src/app/shared/enums/zoning-code-status';
import { getUnitSystemStr, UnitSystem } from 'src/app/shared/enums/unit-system-enum';

export class ZoningCode extends ClonableItem<IZoningCode> implements IZoningCode {
    Id: number;
    Name: string;
    GridicsId: string;
    OldGridicsId: number;
    PropertyRecordsCount: number;
    ActualVersion: number;
    isDeleting: boolean = false;
    CityId?: number;
    EffectiveDate: string;
    LastReviewedDate: string;
    ZoningRegulationName: string;
    ZoningRegulationLink: string;
    Status?: number;

    private _versions: ZoningCodeVersion[] = [];
    areVersionsLoaded = false;
    Tasks: ITaskModel[] = [];
    Statistic?: IZoningCodeStatistic[] = [];
    ZoningCodeRecord?: IZoningCodeRecord;
    StateName?: string;
    UnitSystem?: UnitSystem;
    isOrphan?: boolean;

    get Versions() {
        return this._versions;
    }

    get IndexingTask() {
        return this.Tasks.find(task => task.isIndexing);
    }

    set Versions(value: ZoningCodeVersion[]) {
        this._versions = value;
        this.areVersionsLoaded = true;
    }

    get hasVersionsTasks() {
        return !!this.Versions.find(version => version.hasTasks);
    }

    get hasTasksZC() {
        return this.Tasks.length > 0;
    }

    get taskOrStatistic(): ITaskModel | IZoningCodeStatistic {
        return this.Tasks.length ? this.Tasks[0] : this.Statistic[0];
    }

    get isPRCountUpdated() {
        return this.PropertyRecordsCount != -1;
    }

    get effectiveDateFormatted(): string {
        return this.EffectiveDate ? moment(this.EffectiveDate).format(DATE_FORMAT) : '';
    }

    get lastReviewedDateFormatted(): string {
        return this.LastReviewedDate ? moment(this.LastReviewedDate).format(DATE_FORMAT) : '';
    }

    get cachedPRsCount(): number {
        return _.get(this, 'ZoningCodeRecord.MetaData.cachedPRs.total');
    }

    get propertyRecordsCount(): number {
        return this.cachedPRsCount || this.PropertyRecordsCount;
    }

    get zoningCodeStatus(): ZoningCodeStatus {
        return _.get(this, 'ZoningCodeRecord.Status', 0);
    }

    get zoningCodeStatusStr(): string {
        return getPrettyZCStatus(this.zoningCodeStatus);
    }

    get unitSystemStr(): string {
        return getUnitSystemStr(this.UnitSystem);
    }

    updateTasksFromList(tasks: ITaskModel[]) {
        this.Tasks =
            tasks && tasks.length
                ? tasks.filter(task => task.zoningCodeId === this.Id && task.isIndexing && !task.isStep)
                : [];
        this.Versions.map(version => version.updateTasksFromList(tasks));
        this.PropertyRecordsCount = _.get(this, 'Tasks[0].totalPropertyRecords', this.PropertyRecordsCount);
    }

    hasTasks() {
        const versionWithTasks = this.Versions.find(version => {
            return !!version.Tasks.length;
        });

        return !!this.Tasks.length || !!versionWithTasks;
    }

    setDeletionStatus() {
        this.isDeleting = true;
    }

    setStatistic(statistic: IZoningCodeStatistic) {
        this.Statistic[0] = statistic;
        this.PropertyRecordsCount = this.Statistic[0]
            ? this.Statistic[0].totalPropertyRecords
            : this.PropertyRecordsCount;
    }

    unregisterVersionById(zcVersionId) {
        if (!_.isNumber(zcVersionId)) {
            return;
        }

        this.Versions = this.Versions.filter(zcVersion => zcVersion.Id !== zcVersionId);
    }

    setZoningCodeRecord(zcRecord: IZoningCodeRecord) {
        this.ZoningCodeRecord = zcRecord;
    }
}

export function ZoningCodeFactory(data: IZoningCode) {
    return new ZoningCode(data);
}
